import { useEffect, useRef, useState } from "react";
import { nextMobileRatingFocusIndex } from "./ratingTrayFocus";
import { TIERS } from "./tiers";
import type { Rating } from "./types";

interface MobileRatingTrayProps {
  commonRating: Rating | undefined;
  focusOnOpen: boolean;
  focusRequest: number;
  label: string;
  onDismiss: () => void;
  onRate: (rating: Rating) => void;
}

export function MobileRatingTray({ commonRating, focusOnOpen, focusRequest, label, onDismiss, onRate }: MobileRatingTrayProps) {
  const checkedIndex = TIERS.findIndex((tier) => tier.value === commonRating);
  const [focusIndex, setFocusIndex] = useState(checkedIndex >= 0 ? checkedIndex : 0);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnOpen) return;
    const nextIndex = checkedIndex >= 0 ? checkedIndex : 0;
    optionsRef.current?.querySelectorAll<HTMLButtonElement>(".mobile-rating-option")[nextIndex]?.focus();
  }, [checkedIndex, focusOnOpen, focusRequest]);

  return (
    <section
      className="mobile-rating-tray"
      aria-label="Quick neighborhood rating"
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.preventDefault();
        onDismiss();
      }}
    >
      <div className="mobile-rating-tray-heading">
        <span><small>Quick rate</small><strong>{label}</strong></span>
        <button type="button" onClick={onDismiss} aria-label="Close quick rating">×</button>
      </div>
      <div ref={optionsRef} className="mobile-rating-options" role="group" aria-label={`Rate ${label}`}>
        {TIERS.map((tier, index) => {
          const checked = commonRating === tier.value;
          return (
            <button
              key={tier.value}
              type="button"
              aria-pressed={checked}
              className="mobile-rating-option"
              data-active={checked ? "true" : "false"}
              data-rating={tier.value}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => onRate(tier.value)}
              onFocus={() => setFocusIndex(index)}
              onKeyDown={(event) => {
                const keys = ["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Home", "End"];
                if (!keys.includes(event.key)) return;
                event.preventDefault();
                const nextIndex = nextMobileRatingFocusIndex(index, event.key, TIERS.length);
                setFocusIndex(nextIndex);
                event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(".mobile-rating-option")[nextIndex]?.focus();
              }}
            >
              <i aria-hidden="true" />
              <span>{tier.shortLabel}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
